import React, { FC } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  ResetPasswordForm,
  ResetPasswordFormValues,
} from 'src/components/Forms/ResetPasswordForm';
import { resetPasswordFormSchema } from 'src/components/Forms/validationSchemas';
import { Link } from 'src/components/Link/styled';
import { Modal } from 'src/components/Modals';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { RESET_PASSWORD } from 'src/utils/gql/mutations';
import { Mutation, MutationResetPasswordArgs } from 'src/utils/gql/types';

export const ResetPassword: FC = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const formMethods = useForm<ResetPasswordFormValues>({
    mode: 'onChange',
    resolver: yupResolver(resetPasswordFormSchema),
    criteriaMode: 'all',
    defaultValues: { email: '' },
  });

  const [resetPassword, { loading }] = useMutation<
    Pick<Mutation, 'resetPassword'>,
    MutationResetPasswordArgs
  >(RESET_PASSWORD, {
    onCompleted: () =>
      snackbar(
        'Your password reset request has been processed. Please check your email.',
        'success',
      ),
    onError: (err) => {
      const { graphQLErrors, message: errorText } = err as ApolloError;
      const message =
        graphQLErrors && graphQLErrors.length > 0
          ? graphQLErrors[0].message
          : errorText;

      snackbar(message);
    },
  });

  const handleSubmit: SubmitHandler<ResetPasswordFormValues> = ({ email }) =>
    resetPassword({ variables: { email } });

  return (
    <Modal open title="Kipify" hideBackdrop>
      <>
        <Typography
          variant="h2"
          textAlign="center"
          mb={isSmallScreen ? '25px' : '40px'}
        >
          Reset Your Password
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          maxWidth="410px"
          mb={isSmallScreen ? '20px' : '40px'}
        >
          Please enter the email address to which you'd like your password reset
          information sent.
        </Typography>
        <FormProvider {...formMethods}>
          <ResetPasswordForm onSubmit={handleSubmit} loading={loading} />
        </FormProvider>
        <Link to={`/${ROUTE_SOURCES.LOGIN}`}>Go back to Sign In</Link>
      </>
    </Modal>
  );
};
